// Deals page data: the three master-admin report endpoints and the formatting
// rules every tab shares. Money from the API is PENCE.
import axios from "axios";

const SILENT = { metadata: { silent: true } };

export const fetchPrivateSellers = async ({ date, region } = {}) => {
  const { data } = await axios.get("/api/admin/reports/buy-opportunities", { params: { date: date || undefined, region: region || undefined }, ...SILENT });
  return data;
};

export const fetchBelowTrade = async ({ days = 7, type = "all", q = "", region = null } = {}) => {
  // 500 = the API's max; the default 200 cut a busy week off without saying so.
  const { data } = await axios.get("/api/admin/reports/below-trade", { params: { days, type, q: q || undefined, region: region || undefined, limit: 500 }, ...SILENT });
  return data;
};

export const fetchAuctions = async ({ withinHours = 6 } = {}) => {
  const { data } = await axios.get("/api/admin/reports/auctions", { params: { within_hours: withinHours }, ...SILENT });
  return data;
};

// Deals → "Contact seller": first enquiries to Chrono24 sellers, sent by the sender box
// from the connected Chrono24 account (backend .claude/features/ch24-contact-seller.md).
export const fetchOutreach = async () => (await axios.get("/api/admin/ch24-outreach", SILENT)).data;
export const queueOutreach = async (body) => (await axios.post("/api/admin/ch24-outreach", body, SILENT)).data;
export const connectCh24 = async (email, password) => (await axios.post("/api/admin/ch24-outreach/connect", { email, password }, SILENT)).data;
export const sendCh24Code = async (code) => (await axios.post("/api/admin/ch24-outreach/code", { code }, SILENT)).data;
export const checkCh24 = async () => (await axios.post("/api/admin/ch24-outreach/check", {}, SILENT)).data;
export const markOutreachSent = async (id, conversation) => (await axios.post(`/api/admin/ch24-outreach/${id}/mark-sent`, { conversation }, SILENT)).data;

// The Chrono24 listing id from a listing link (".../anything--id48980066.htm"); null for
// anything else. Same rule as the API (Ch24Outreach::listingIdFromUrl).
export const ch24ListingId = (url) => {
  const m = /^https?:\/\/(www\.)?chrono24\.(com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})\/.*--id(\d+)\.htm/i.exec(String(url || ""));
  return m ? Number(m[3]) : null;
};

export const money = (pence) =>
  pence === null || pence === undefined || pence === "" || !Number.isFinite(Number(pence))
    ? "—"
    : `£${Math.round(Number(pence) / 100).toLocaleString("en-GB")}`;

// Original-currency amount (pence), e.g. "HK$114,000", when a price is not in GBP.
export const moneyIn = (pence, currency) => {
  if (pence === null || pence === undefined || !Number.isFinite(Number(pence))) return "—";
  const major = Math.round(Number(pence) / 100);
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: String(currency || "GBP").toUpperCase(), maximumFractionDigits: 0 }).format(major);
  } catch {
    return `${String(currency || "").toUpperCase()} ${major.toLocaleString("en-GB")}`;
  }
};

// Trade Feed searches message text, where dealers write the reference without the
// catalogue's dial/variant suffix: "126711CHNR" not "126711chnr-0002", "15510ST"
// not "15510ST.OO.1320ST.06". Dotted Omega-style references are left whole.
export const tradeFeedQuery = (reference) => String(reference || "").trim()
  .replace(/-[A-Z0-9]+$/i, "")
  .replace(/\.[A-Z]{2}\..*$/i, "");

// Private-seller gap: % the 3M trade median is ABOVE the listing price (the
// report's own rule) — positive = the listing is under the trade price.
// Tones follow the design: 10%+ under red, 0–10% under amber, at/over green.
export const gapOf = (pct) => {
  if (pct === null || pct === undefined || !Number.isFinite(Number(pct))) return null;
  const n = Number(pct);
  const tone = n >= 10 ? "hot" : n > 0 ? "warm" : "cool";
  const label = n > 0 ? `${n.toFixed(1)}% under` : `${Math.abs(n).toFixed(1)}% over`;
  return { tone, label };
};

// The same gap as a short pill ("−14%" under, "+28%" over), for the Private sellers card.
export const shortGap = (pct) => {
  const n = Number(pct);
  if (pct === null || pct === undefined || !Number.isFinite(n)) return null;
  return n > 0 ? `−${Math.round(n)}%` : `+${Math.round(Math.abs(n))}%`;
};

// % a price sits under (+) or over (−) a median, both pence: (median − price) / price, the
// report's own discount_pct rule. Null when either is missing.
export const gapPct = (median, price) => {
  const m = Number(median);
  const p = Number(price);
  return median != null && price != null && m > 0 && p > 0 ? ((m - p) / p) * 100 : null;
};

// How long a private-seller listing has been in the report, from the API's `history`
// (BuyOpportunityReport::history): new = first seen in this report, week = 2–7 days,
// recurring = 8+ days. Without history, `is_fresh` (listed in the last 48h) counts as new.
export const ageOf = (o) => {
  const days = o?.history?.days_listed ?? (o?.is_fresh ? 1 : null);
  if (!days) return null;
  const kind = days <= 1 ? "new" : days < 8 ? "week" : "recurring";
  return { days, kind, label: kind === "new" ? "New" : kind === "week" ? `${days}d` : `↻ ${days}d` };
};

// ★ Top pick (Alex, 2026-10-29: one label, criteria agreed in chat):
//   - 10–40% under the 3M trade median (more than 40% is usually a wrong reference match);
//   - a trade median exists and is not flagged thin history;
//   - demand: a core brand, or at least one B2B lead for the reference.
// "Not already contacted" and the per-region cap are applied in topPicks().
export const TOP_PICK = { minGap: 10, maxGap: 40, perRegion: 5 };

export const isTopPick = (o) => {
  const pct = Number(o?.discount_pct);
  if (o?.discount_pct === null || o?.discount_pct === undefined || !Number.isFinite(pct)) return false;
  if (pct < TOP_PICK.minGap || pct > TOP_PICK.maxGap) return false;
  if (!o.trade_median_3m || o.thin_history) return false;
  return o.brand_group === "core" || Number(o.b2b_leads) > 0;
};

// The day's top picks: eligible and not contacted, biggest £ room under trade first (then B2B
// leads, then newest), at most TOP_PICK.perRegion per region. `contacted` = Set of Chrono24
// listing ids already messaged.
export const topPicks = (rows, contacted = new Set()) => {
  const room = (o) => Number(o.trade_median_3m) - Number(o.price_gbp ?? o.listing_price);
  const eligible = rows
    .filter((o) => isTopPick(o) && !contacted.has(ch24ListingId(o.listing_url)))
    .sort((a, b) => room(b) - room(a)
      || (Number(b.b2b_leads) || 0) - (Number(a.b2b_leads) || 0)
      || (a.history?.days_listed ?? 99) - (b.history?.days_listed ?? 99));
  const perRegion = {};
  return eligible.filter((o) => {
    perRegion[o.region] = (perRegion[o.region] || 0) + 1;
    return perRegion[o.region] <= TOP_PICK.perRegion;
  });
};

// flags: react-country-flag codes, as on Arbitrage (Asia = Hong Kong, Middle East = Dubai).
export const REGIONS = [
  { value: "uk", label: "UK", flags: ["GB"] },
  { value: "eu", label: "Europe", flags: ["EU"] },
  { value: "other", label: "USA · Asia · Middle East", flags: ["US", "HK", "AE"] },
];

// react-country-flag code for a region name ("europe", the report's `country` / Underpriced's
// `region`) or an ISO code; null when there is no flag (africa, unknown).
const FLAG_CODES = { uk: "GB", gb: "GB", europe: "EU", eu: "EU", usa: "US", asia: "HK", "middle-east": "AE" };
export const flagCode = (country) => {
  const c = String(country || "").trim().toLowerCase();
  return FLAG_CODES[c] || (/^[a-z]{2}$/.test(c) ? c.toUpperCase() : null);
};

// Days since a price date, in the viewer's local days: 0 = today. Null without a date.
export const daysAgo = (iso, now = new Date()) => {
  if (!iso) return null;
  const d = new Date(String(iso).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((new Date(now).setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000));
};

const FLAGS = { uk: "UK", europe: "EU", usa: "US", asia: "Asia", "middle-east": "ME", africa: "Africa" };
export const regionTag = (country) => FLAGS[String(country || "").toLowerCase()] || String(country || "").toUpperCase();

// Underpriced price regions: the API's normalised product_prices.location keys (as on Arbitrage).
const REGION_NAMES = { uk: "UK", europe: "Europe", usa: "USA", asia: "Asia", "middle-east": "Middle East", africa: "Africa" };
export const regionName = (key) => REGION_NAMES[key]
  || String(key || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export const GROUPS = [
  { value: "core", label: "Core brands" },
  { value: "independent", label: "Independent" },
  { value: "rest", label: "Rest" },
];

export const daowatchesPreview = (productId) => (productId ? `https://daowatches.com/request/sell?preview=${productId}` : null);

const D = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/London" });
const T = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
export const reportDateLabel = (iso) => (iso ? D.format(new Date(`${String(iso).slice(0, 10)}T12:00:00Z`)) : "—");
export const timeLabel = (iso) => (iso ? T.format(new Date(iso)) : "—");
export const seenLabel = (iso) => {
  if (!iso) return "—";
  const d = new Date(String(iso).replace(" ", "T"));
  const today = new Date();
  const days = Math.floor((today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (days <= 0) return `Today ${T.format(d)}`;
  if (days === 1) return "Yesterday";
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(d);
};

// Countdown text + urgency for an auction lot.
export const timeLeft = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const label = s <= 0 ? "Ended" : d > 0 ? `${d}d ${h % 24}h` : h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
  const tone = s <= 3600 ? "hot" : s <= 3 * 3600 ? "warm" : "plain";
  return { label, tone };
};

export const SOURCE_TONE = { trade: "trade", chrono24: "chrono", watch_finder: "wf", watch_collecting: "wc", instagram: "ig" };
